import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EntityStore, ID } from '@datorama/akita';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { tap, map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/enviroment.prod';
import { FinalProductWindows } from 'src/app/models/final_product_windows.model';
import { TabCategory } from 'src/app/models/tab_category.model';
import { CategoryContent } from 'src/app/models/category_content-model';
import { CategoryContentStore } from '../akita/category-content.store';
import { CategoryStore } from '../akita/category.store';
import { Layers } from 'src/app/models/layers.model';
import { LayersStore } from 'src/app/components/layers/services/layers.store';
import { ImageStore } from 'src/app/components/layers/services/images.store';
import { ImageOutput } from 'src/app/models/image-output.model';
import { FinalProduct } from 'src/app/models/final_product.model';
import { SetEntities } from '@datorama/akita/src/lib/setEntities';
import { DefaultSelection } from 'src/app/models/default-selection.model';
import { Material } from 'src/app/models/material.model';
import { FinalProductQuery } from '../final_product/akita/FinalProduct/FinalProduct.query';
import { FinalProductStore } from '../final_product/akita/FinalProduct/FinalProduct.store';
import { DefaultSelectionQuery } from '../final_product/akita/DefaultOptions/default.query';
import { DefaultSelectionStore } from '../final_product/akita/DefaultOptions/default.store';
import { WindowsQuery } from '../akita/tabs.query';
import { WindowsStore } from '../akita/tabs.store';
import { MPCode } from 'src/app/models/MPCode.model';
import { MaterialStore } from '../akita/materials/materials.store';
import { MaterialQuery } from '../akita/materials/materials.query';
import { MaterialRule } from 'src/app/models/MaterialRule.model';
import { MaterialRuleQuery } from '../akita/materialRules/materialRule.query';
import { MaterialRuleStore } from '../akita/materialRules/materialRule.store';
import { CategoryQuery } from '../akita/category.query';
import { NavBarService } from '../navbar/navbar.service';
import { Tools } from 'src/app/models/tools.model';
import { ToolTypes } from 'src/app/models/ToolTypes.model';
import { LocalProductRef, ProductLimits } from 'src/app/models/product-limits.model';

export { ProductLimits };

@Injectable({
  providedIn: 'root'
})
export class ProductTabsService {
  private _windows = new BehaviorSubject<FinalProductWindows[]>([]);
  private _categories = new BehaviorSubject<TabCategory[]>([]);
  private singleCategory = new BehaviorSubject<TabCategory | null>(null);
  private _categoryContent = new BehaviorSubject<CategoryContent[]>([]);
  private _defaultSelections = new BehaviorSubject<DefaultSelection[]>([]);
  private _categoryContentSelected = new BehaviorSubject<CategoryContent | null>(null);
  private singeCatCon = new BehaviorSubject<CategoryContent| null>(null);
  private lastSelectedTabIndexSubject = new BehaviorSubject<number>(0);
  private categoryContentSelectedSubject = new BehaviorSubject<boolean>(false);
  private CategoriesCache: { [categoryId: number]: TabCategory[] } = {};
  private ContentCache: { [categoryId: number]: CategoryContent[] } = {};
  private LayersCache: FinalProduct[] = [];
  // Keyed by final product. This used to be a single flat array with an
  // early return that ignored the id, so a second configuration on a
  // different final product rendered the first product's layers.
  private ImagesCache: { [finalProductId: number]: ImageOutput[] } = {};
  private selectedToolTypes = new BehaviorSubject<any[]>([]);

  lastSelectedTabIndex$ = this.lastSelectedTabIndexSubject.asObservable();
  categoryContentSelected$ = this.categoryContentSelectedSubject.asObservable();


  private _selected = new BehaviorSubject<{  Content: CategoryContent, Category: TabCategory }[]>([]);
  selected$ = this._selected.asObservable();

  private _materialValues = new BehaviorSubject<{  Name: string , Value: string, card_place_id: number, id:number }[]>([]);
  $materialValues = this._materialValues.asObservable();

  private _productLimits = new BehaviorSubject<ProductLimits[]>([]);
  $productLimits = this._productLimits.asObservable();

  // Every local product (CQ/LQ/EQ/TCQ) of the current final product, tagged with
  // its type (the four tables' ids overlap). Needed to map a productLimits entry
  // back to the category-contents it came from, so the PDF can show "pcs =" per
  // category-content.
  private _localProducts = new BehaviorSubject<LocalProductRef[]>([]);
  $localProducts = this._localProducts.asObservable();

  private _mpCode = new BehaviorSubject<MPCode>(new MPCode(0, [], '', '', 0));
  $mpCode = this._mpCode.asObservable();

  private _additionalFields = new BehaviorSubject<{ type: string, value: any, label?: string }[]>([]);
  $additionalFields = this._additionalFields.asObservable();

  private _backgroundColor = new BehaviorSubject<string>("#FFFFFF");
  backgroundColor$ = this._backgroundColor.asObservable();

  private backgroundIdSubject = new BehaviorSubject<number | null>(null);
  backgroundId$ = this.backgroundIdSubject.asObservable();

  constructor(
    private http: HttpClient,
    private categoryContentStore: CategoryContentStore,
    private categoryStore: CategoryStore,
    private categoryQuery: CategoryQuery,
    private imageStore: ImageStore,
    private finalProductStore: FinalProductStore,
    private finalProductQuery: FinalProductQuery,
    private defaultSelectionStore: DefaultSelectionStore,
    private defaultSelectionQuery: DefaultSelectionQuery,
    private windowsStore: WindowsStore,
    private windowsQuery: WindowsQuery,
    private materialStore: MaterialStore,
    private materialQuery: MaterialQuery,
    private materialRuleQuery: MaterialRuleQuery,
    private materialRuleStore: MaterialRuleStore,
    private NavBarService: NavBarService
  ) { }

  setSelectedBackgroundId(backgroundId: number): void {
    this.backgroundIdSubject.next(backgroundId);
  }

  getSelectedToolTypes(): Observable<ToolTypes[]> {
    return this.selectedToolTypes.asObservable();
  }

  getCurrentSelectedToolTypes(): ToolTypes[] {
    return this.selectedToolTypes.getValue();
  }

  // Method to add a new tool type to the array
  addSelectedToolType(toolType: ToolTypes): void {
    const currentTypes = this.selectedToolTypes.getValue();

    // Check if a selection with the same tool_id already exists
    const updatedTypes = currentTypes.filter(type => type.tool_id !== toolType.tool_id);

    // Add the new selection, replacing any existing one with the same tool_id
    updatedTypes.push(toolType);

    // Update the BehaviorSubject with the new array
    this.selectedToolTypes.next(updatedTypes);
  }

  // Method to remove a tool type from the array by ID
  removeSelectedToolType(toolTypeId: number): void {
    const updatedTypes = this.selectedToolTypes.getValue().filter(type => type.id !== toolTypeId);
    this.selectedToolTypes.next(updatedTypes);
  }

  // Method to clear all selected tool types
  clearSelectedToolTypes(): void {
    this.selectedToolTypes.next([]);
  }
  get windows() {
    return this._windows.asObservable();
  }

  private _layers = new BehaviorSubject<FinalProduct[]>([]);

  get categories() {
    return this._categories.asObservable();
  }

  get singleCategories() {
    return this.singleCategory.asObservable();
  }

  get categoryContent() {
    return this._categoryContent.asObservable();
  }
  layers$ = this._layers.asObservable();

  get categoryContentSelected(): Observable<CategoryContent | null> {
    return this._categoryContentSelected.asObservable();
  }


  get layers() {
    return this._layers.asObservable();
  }


  getAllWithQuery(queryParams: string)  {

    return this.http.get<{ data: CategoryContent[] }>(environment.apiLink + `/final_product/${queryParams}/category_content`)
    .pipe(
      tap(response => {
        this._categoryContent.next(response.data);
      })
    )
  }


  updateTabState(tabIndex: number, categoryContentSelected: boolean) {
    this.lastSelectedTabIndexSubject.next(tabIndex);
    this.categoryContentSelectedSubject.next(categoryContentSelected);
  }

  fetchWindowsForProduct(productId: number): Observable<FinalProductWindows[]> {
    const cachedWindows = this.windowsQuery.getByFinalProductId(productId);

    if (cachedWindows.length > 0) {
      // Return cached data if exists
      return of(cachedWindows);
    } else {
      // Fetch from API and cache the response
      return this.http.get<{ data: FinalProductWindows[] }>(`${environment.apiLink}/final_product/${productId}/products-cards`).pipe(
        map(response => response.data ?? []),
        tap(data => {
          if (data.length > 0) {
            // Store data first to prevent the infinite loop issue
            this.windowsStore.set(data);
            this.windowsStore.update({ finalProductId: productId });
          }
        })
      );
    }
  }


  fetchWindowsWithoutCache(productId: number): Observable<FinalProductWindows[]> {
    return this.http.get<{ data: FinalProductWindows[] }>(`${environment.apiLink}/final_product/${productId}/products-cards`).pipe(
      map(response => response.data ?? []),
      tap(data => {
      })
    );
  }


  fetchCategories(categoryId: number) {
    return this.http.get<{ data: TabCategory[] }>(environment.apiLink + `/card-place/?id=${categoryId}`).pipe(
        tap(categories => {
          this._categories.next(categories.data);
        })
      )

  }

  getAllCategoriesWithQuery(queryParams: string) {
    const existingData = this.categoryQuery.getByFinalProductId(Number(queryParams));

    if (existingData.length > 0) {
      return of({ data: existingData });
    } else {
      return this.categoryStore.fetchAndStoreCategory(Number(queryParams), queryParams);
    }
  }


  private _fetchedMaterials = new BehaviorSubject<Material[]>([]);

  get fetchedMaterias(){
    return this._fetchedMaterials.asObservable();
  }


  fetchTools(finalProductId: number): Observable<Tools[]>{
    return this.http.get<{ data: Tools[] }>(`${environment.apiLink}/tools?final_product_id=${finalProductId}`).pipe(
      map(response => response.data),

    );
  }


  fetchMaterials(finalProductId: number): Observable<Material[]> {
    if (this.materialQuery.hasMaterialsForFinalProduct(finalProductId)) {
      return this.materialQuery.selectMaterialsByFinalProductId(finalProductId);
    }

    return this.http.get<{ data: Material[] }>(`${environment.apiLink}/final_product/${finalProductId}/materials`).pipe(
      map(response => response.data ?? []),
      tap(materials => {
        this.materialStore.update({ finalProductId });

      })
    );
  }

  fetchMaterialRules(finalProductId: number): Observable<any> {
    const cachedRules = this.materialRuleQuery.selectMaterialRulesByProductId(finalProductId);

    if (this.materialRuleQuery.hasEntity() && this.materialRuleQuery.getValue().finalProductId === finalProductId) {
      this.NavBarService.setLoading(false);
      return cachedRules;
    }

    return this.http.get<{ data: MaterialRule[] }>(`${environment.apiLink}/material-rules?final_product_id=${finalProductId}`).pipe(
      tap(response => {
        this.materialRuleStore.set(response);
        this.materialRuleStore.update({ finalProductId });
        this.NavBarService.setLoading(false);

      })
    );
  }




  fetchSingleCategory(categoryId: number) {
    return this.http.get<{ data: TabCategory }>(environment.apiLink + `/card-place/${categoryId}`).pipe(
        tap(categories => {
          this.singleCategory.next(categories.data);
        })
      )
  }

  fetchCategoryContent(categoryId: ID): Observable<CategoryContent[]> {
    if (this.ContentCache[+categoryId]) {
      return of(this.ContentCache[+categoryId]);
    }

    this.categoryContentStore.setLoading(true);

    return this.http.get<{ data: CategoryContent[] }>(`${environment.apiLink}/place-choices/?id=${categoryId}`).pipe(
      map((response: { data: CategoryContent[] }) => response.data),
      tap(categoryContent => {
        this.ContentCache[+categoryId] = categoryContent;
        this.categoryContentStore.set(categoryContent);
        this.categoryContentStore.setLoading(false);
      })
    );
  }

  singleFetchCatCon(id:number[]): Observable<CategoryContent> {
    return this.http.get<{ data: CategoryContent }>(environment.apiLink + `/place-choices/${id}`).pipe(
          map(response => response.data), // Extract the 'data' property from the response
          tap(categoryContent => {
            this.singeCatCon.next(categoryContent);
          }),
        )
      }

      fetchLayers(id: number): Observable<FinalProduct> {
        const cachedProduct = this.finalProductQuery.getEntity(id);

        if (cachedProduct) {
          return of(cachedProduct);
        }

        return this.http.get<{ data: FinalProduct }>(`${environment.apiLink}/final_product/${id}`)
          .pipe(
            map(response => response.data),
            tap(product => {
              this.finalProductStore.upsert(id, product);
            }),
            catchError(error => {
              console.error('Failed to fetch final product:', error);
              return throwError(() => new Error('Failed to fetch final product'));
            })
          );
      }

  fetchImageLayers(id: number): Observable<ImageOutput[]> {
    const cached = this.ImagesCache[id];

    if (cached && cached.length > 0) {
        this.imageStore.set(cached);
        return of(cached);
    }

    this.imageStore.setLoading(true);

    return this.http.get<{ data: ImageOutput[] }>(`${environment.apiLink}/final_product_layers/${id}/output`)
    .pipe(
        map(response => response.data),
        tap(response => {
            this.ImagesCache[id] = response;
            this.imageStore.set(response);
            this.imageStore.setLoading(false);
        })
    );
  }

  fetchDefaultSelections(finalProductId: number): Observable<DefaultSelection[]> {
    // First, attempt to select default selections that are specific to the given finalProductId
    return this.defaultSelectionQuery.selectAll({
      filterBy: entity => entity.finalProductId === finalProductId // Add a filter for finalProductId
    }).pipe(
      switchMap(defaultSelections => {
        if (defaultSelections.length === 0) {
          // No cached data, make the API call to fetch data for the specific finalProductId
          return this.http.get<{ data: DefaultSelection[] }>(`${environment.apiLink}/default_selections?finalProductId=${finalProductId}`).pipe(
            map(response => response.data ?? []),
            tap(response => {
              // Set the data in the store, associating it with the finalProductId
              this.defaultSelectionStore.set(response);
            })
          );
        } else {
          // Return the already cached data for the finalProductId
          return this.defaultSelectionQuery.selectAll({
            filterBy: entity => entity.finalProductId === finalProductId // Ensure it returns only data for that product
          });
        }
      })
    );
  }

setBackground(color: string): void {
  this._backgroundColor.next(color);
}

setSelectedData(data: { Content: CategoryContent, Category: TabCategory }[]): void {
  this._selected.next(data);
}

setMaterialValues(newData: { Name: string, Value: string, card_place_id: number, id: number }[]): void {
  const currentData = this._materialValues.getValue();

  // Create a map of existing data for easy lookup
  const dataMap = new Map(currentData.map(item => [item.id, item]));

  // Update or add new data to the map
  newData.forEach(item => {
      dataMap.set(item.id, item);
  });

  // Convert map back to an array
  const mergedData = Array.from(dataMap.values());
  this._materialValues.next(mergedData);

}


setProductLimits(data: ProductLimits[]): void {
  this._productLimits.next(data);
}

setLocalProducts(data: LocalProductRef[]): void {
  this._localProducts.next(data);
}

getCurrentLocalProducts(): LocalProductRef[] {
  return this._localProducts.getValue();
}

setMPCode(data: MPCode): void {
  this._mpCode.next(data);

}

setAdditionalFields(data: { type: string, value: any, label?: string }[]): void {
  this._additionalFields.next(data);
}

getAdditionalFields(): Observable<{ type: string, value: any, label?: string }[]> {
  return this.$additionalFields;
}

selectCategoryContent(categoryContent: CategoryContent | null): void {
  this._categoryContentSelected.next(categoryContent);
}

/**
 * Replace material values outright.
 *
 * setMaterialValues() merges by id and never removes an entry, which is
 * correct while editing one configuration but leaks every previous value
 * into the next one. Snapshot restore and reset both need a true replace.
 */
replaceMaterialValues(data: { Name: string, Value: string, card_place_id: number, id: number }[]): void {
  this._materialValues.next(data);
}

setSelectedToolTypes(types: ToolTypes[]): void {
  this.selectedToolTypes.next(types ?? []);
}

/* ------------------------------------------------------------------ *
 * Synchronous reads.
 *
 * Snapshotting a configuration has to happen in one pass at the moment the
 * user adds it to the cart, so every piece of state needs a getValue()-style
 * accessor rather than only an observable.
 * ------------------------------------------------------------------ */

get currentSelected(): { Content: CategoryContent, Category: TabCategory }[] {
  return this._selected.getValue();
}

get currentMaterialValues(): { Name: string, Value: string, card_place_id: number, id: number }[] {
  return this._materialValues.getValue();
}

get currentProductLimits(): ProductLimits[] {
  return this._productLimits.getValue();
}

get currentMPCode(): MPCode {
  return this._mpCode.getValue();
}

get currentAdditionalFields(): { type: string, value: any, label?: string }[] {
  return this._additionalFields.getValue();
}

get currentBackgroundId(): number | null {
  return this.backgroundIdSubject.getValue();
}

get currentBackgroundColor(): string {
  return this._backgroundColor.getValue();
}

get currentTabIndex(): number {
  return this.lastSelectedTabIndexSubject.getValue();
}

/**
 * Clear everything that belongs to one configuration, leaving reference data
 * (windows, categories, default selections) in place.
 *
 * Called when a configuration is added to the cart, when one is opened for
 * editing, and on logout. Without this, configuration #2 inherits #1's
 * materials, limits, MP code, tool types and layer images.
 */
resetConfigurationState(): void {
  this._selected.next([]);
  this._materialValues.next([]);
  this._productLimits.next([]);
  this._localProducts.next([]);
  this._mpCode.next(new MPCode(0, [], '', '', 0));
  this._additionalFields.next([]);
  this.selectedToolTypes.next([]);
  this._categoryContentSelected.next(null);
  this.singeCatCon.next(null);
  this._backgroundColor.next('#FFFFFF');
  this.backgroundIdSubject.next(null);
  this.lastSelectedTabIndexSubject.next(0);
  this.categoryContentSelectedSubject.next(false);

  // Per-product caches. ImagesCache is the one that actually bleeds between
  // products; the others are cleared for consistency.
  this.ContentCache = {};
  this.ImagesCache = {};
  this.CategoriesCache = {};
  this.LayersCache = [];
}

}
